import { Icon, IconName, IconSize, Palette } from '@sbt-web/ui';
import { isBrowser } from '@sbt-web/utils';
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import classes from './index.module.scss.js';

const centreOfItaly = { lat: 41.87194, lng: 12.56738 };
const HereMap = ({ assetsBase, apiKey, selectedPoint, radius }) => {
    // Create a reference to the HTML element we want to put the map on
    const mapRef = useRef(null);
    const [hereCodeLoaded, setHereCodeLoaded] = useState(false);
    // Load the SDK from HERE
    useEffect(() => {
        // If rendered server-side, do not allow the loading and rendering.
        if (!isBrowser()) {
            return;
        }
        if (window.H != undefined) {
            setHereCodeLoaded(true);
            return;
        }
        try {
            import(
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            /*webpackIgnore: true*/ `${assetsBase}/static/here-maps/here-sdk-313011.js`
            /* eslint-enable @typescript-eslint/ban-ts-comment */
            )
                .then(() => {
                setHereCodeLoaded(true);
            })
                .catch(() => {
                console.error('Unable to load HERE maps code.');
                setHereCodeLoaded(false);
            });
        }
        catch {
            console.error('Import failed, unable to load HERE Maps code');
        }
    }, [assetsBase, setHereCodeLoaded]);
    const [hereMap, setHereMap] = useState(null);
    // Create the map, if the SDK code is ready
    useLayoutEffect(() => {
        if (!mapRef.current) {
            return;
        }
        if (hereCodeLoaded) {
            const H = window.H;
            const platform = new H.service.Platform({
                apikey: apiKey,
            });
            const defaultLayers = platform.createDefaultLayers({
                ppi: 250,
                lg: 'it-IT',
                crossOrigin: 'anonymous',
            });
            const topPadding = 24;
            const sidePadding = 24;
            const uiElementHeight = 204;
            const spaceAboveSlider = 24;
            const hm = new H.Map(mapRef.current, defaultLayers.vector.normal.map, {
                padding: {
                    top: topPadding,
                    right: sidePadding,
                    bottom: uiElementHeight + spaceAboveSlider,
                    left: sidePadding,
                },
                pixelRatio: window.devicePixelRatio || 1,
                center: centreOfItaly,
                zoom: 5,
            });
            setHereMap(hm);
            // This will act as a cleanup to run once this hook runs again.
            // This includes when the component un-mounts
            return () => {
                hm.dispose();
            };
        }
    }, [hereCodeLoaded, apiKey, setHereMap]);
    // Set the center and marker on the map
    useEffect(() => {
        if (hereMap == null) {
            return;
        }
        hereMap.setCenter({ lat: selectedPoint.lat, lng: selectedPoint.lng });
        const icon = new H.map.Icon(renderToStaticMarkup(React.createElement(Icon, { name: IconName.Pin, iconSize: IconSize.MD, fillColor: Palette.PurpleImmobili })), {
            size: { w: 32, h: 32 },
            crossOrigin: false,
        });
        // Create a marker using the previously instantiated icon:
        const marker = new H.map.Marker({
            lat: selectedPoint.lat,
            lng: selectedPoint.lng,
        }, { icon });
        // Add the marker to the map:
        hereMap.addObject(marker);
        return () => {
            if (hereMap?.getObjects().includes(marker)) {
                hereMap?.removeObject(marker);
            }
        };
    }, [hereMap, selectedPoint.lat, selectedPoint.lng]);
    // Draw the radius and zoom to see the entire circle.
    useEffect(() => {
        if (hereMap == null) {
            return;
        }
        const circle = new window.H.map.Circle(
        // The central point of the circle
        { lat: selectedPoint.lat, lng: selectedPoint.lng }, 
        // The radius of the circle in meters
        radius, {
            style: {
                lineWidth: 2,
                strokeColor: Palette.PurpleImmobili, // Color of the perimeter
                fillColor: 'rgba(162, 89, 255, 0.1)', // Color of the circle
            },
        });
        hereMap.addObject(circle);
        hereMap.getViewModel().setLookAtData({
            bounds: circle.getBoundingBox(),
        });
        return () => {
            if (hereMap?.getObjects().includes(circle)) {
                hereMap?.removeObject(circle);
            }
        };
    }, [hereMap, radius, selectedPoint.lat, selectedPoint.lng]);
    return React.createElement("div", { className: classes['here-map'], ref: mapRef });
};

export { HereMap as default };
//# sourceMappingURL=HereMap.js.map
